import { useForm } from "@tanstack/react-form";

import { useCall } from "@/hooks/useCall/useCall";
import { useTrack } from "@/hooks/useTrack/useTrack";

import {
  CreateEvaluationInput,
  EvaluationCreateFormProps,
} from "./EvaluationCreateForm.decl";
import { initCategories } from "./EvaluationCreateForm.utility";

export function useTracking() {
  const { call } = useCall();
  const { track } = useTrack();

  const onClick = () => {
    track("call_evaluation_cancelled", {
      call_id: call?.id,
    });
  };

  return { onClick };
}

type UseEvaluationCreateFormParams = Pick<
  EvaluationCreateFormProps,
  "scoreCard" | "revieweeUserId"
> & {
  callUuid: string;
  onSubmit: (value: CreateEvaluationInput) => void;
};

/**
 * Wraps `useForm` for the create-evaluation form so the form-API type is
 * inferred straight from the hook (`ReturnType<typeof useEvaluationCreateForm>`)
 * rather than by hand-spelling react-form v1's 12 (now-undefaulted) generics.
 * This is the idiomatic TanStack Form v1 pattern — see the "Form Composition"
 * guide.
 */
export function useEvaluationCreateForm({
  scoreCard,
  revieweeUserId,
  callUuid,
  onSubmit,
}: UseEvaluationCreateFormParams) {
  return useForm({
    defaultValues: {
      categories: initCategories(scoreCard.categories),
      feedback: "",
      // Below are hidden fields.
      callUuid,
      scoreCardId: scoreCard.ID,
      revieweeUserId,
      scoreCardName: scoreCard.name,
      scoreCardLanguage: scoreCard.language,
      scoreCardDescription: scoreCard.description,
    } as CreateEvaluationInput,
    onSubmit: async ({ value }) => {
      onSubmit(value);
    },
  });
}

export type EvaluationCreateFormApi = ReturnType<
  typeof useEvaluationCreateForm
>;
